<template>
    <div>
        <b-row>
            <b-col cols="5">
                <h3 class="mb-2">Inventory</h3>
                <b-button variant="primary" size="sm" @click.prevent="openForm">
                    <span>New Inventory</span> <i class="fas fa-plus-circle"></i>
                </b-button>
            </b-col>
        </b-row>

        <b-modal ref="editModal" :title="inventory.id ? 'Edit Inventory' : 'Add Inventory'" @ok="submitInformation"
            no-fade centered>
            <b-row>
                <b-col cols="12">
                    <SearchComponent :id="Number(store.id)" v-model="store" :label="`Store`" :prepopulate="true"
                        :search-u-r-l="`/Store`" :disabled="!!inventory.id" />
                </b-col>
                <b-col cols="12">
                    <SearchComponent :id="Number(program.id)" v-model="program" clearable label="Program"
                        :params="{ flags: 'Consigned Inventory' }" prepopulate search-u-r-l="/Program"
                        :disabled="!!inventory.id" />
                </b-col>
                <b-col v-if="hasInventoryLocations" cols="12">
                    <b-checkbox v-model="useForFilling" switch class="pull-right">Use for Filling</b-checkbox>
                    <SearchComponent :id="location?.id" v-model="location" search-u-r-l="/inventory/inventory-locations"
                        prepopulate label="Location" clearable :disabled="!!inventory.id" />
                </b-col>
                <b-col cols="12">
                    <b-form-group label="Quantity">
                        <b-input v-model.number="inventory.quantity" type="number" />
                    </b-form-group>
                </b-col>
                <b-col cols="12" v-if="reasonOptions.length">
                    <b-form-group label="Reason">
                        <b-form-select v-model="reasonSelection" :options="reasonOptions" />
                    </b-form-group>
                </b-col>
            </b-row>
        </b-modal>

        <b-row id="inventory-table-row">
            <b-col>
                <b-table id="inventory-table" :fields="inventoryFields" :items="inventories">
                    <template #cell(location.name)="data">
                        <div>
                            {{ data.value }}
                        </div>
                        <b-badge variant="primary" v-if="data.item.useForFilling">
                            Use for Filling
                        </b-badge>
                    </template>
                    <template #cell(actions)="data">
                        <div class="text-right table-icon-actions">
                            <a href="#" @click.prevent="viewHistory(data.item)" v-b-tooltip="'History'">
                                <i class="fas fa-history" />
                            </a>
                            <a href="#" @click.prevent="updateInventory(data.item)" v-b-tooltip="'Edit'">
                                <b-icon icon="pencil-square" />
                            </a>
                            <a href="#" @click.prevent="deleteInventoryConfirmDialog(data.item)" v-b-tooltip="'Remove'">
                                <b-icon icon="x-circle-fill" variant="danger" />
                            </a>
                        </div>
                    </template>
                </b-table>
            </b-col>
        </b-row>
        <b-modal v-model="showHistory" size="xl"
            :title="`Inventory History at ${store.name}${program?.name?.length > 0 ? ' - ' + program.name : ''}`" no-fade>
            <InventoryHistory id="inventory-history-component" :drug="drug" :store="store" :program="program" />
        </b-modal>
    </div>
</template>

<script lang="ts">
import { Inventory } from '@/models/Inventory';
import { Store } from '@/models/Store';
import { Program } from '@/models/Program';
import { Component, Prop, Vue, Ref } from 'vue-property-decorator';
import { Table, TableColumn } from 'element-ui';
import axios from 'axios';
import SearchComponent from '@/components/SearchComponent.vue';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import InventoryHistory from "@/components/Inventory/InventoryHistory.vue";
import { DrugProduct } from "@/models/Drug/DrugProduct";
import { InventoryLocation } from '@/models/InventoryLocation';
import { BModal } from 'bootstrap-vue';

@Component({
    name: "InventoryComponent",
    components: {
        InventoryHistory,
        SearchComponent,
        [Table.name]: Table,
        [TableColumn.name]: TableColumn
    }
})
export default class InventoryComponent extends Vue {

    @Prop({ default: 0 }) private drugSource!: Number;
    @Prop() private packageId!: string;
    @Prop() private billingUnit!: string;
    @Prop() private inventories!: Inventory[];

    mounted() {
        this.getReasonOptionsFromConfiguration();
        this.loadHasInventoryLocations();
    }

    protected showHistory: boolean = false;

    private store: Store = new Store();
    private program: Program = new Program();

    private inventory: Inventory = new Inventory();
    protected reasonSelection: string | null = null;
    protected location = new InventoryLocation();
    protected useForFilling = false;
    @Ref("editModal") protected editModal!: BModal;

    private reasonOptions: any[] = [];

    async getReasonOptionsFromConfiguration() {
        try {
            const response = await this.$http.get<Array<String>>("/Configuration/InventoryReasons");
            if (response.data && !response.data.length) return;
            this.reasonOptions = [
                { value: null, text: 'Select a reason' },
                ...(response.data ? response.data : ['New Inventory', 'Stock Check'])
            ];
        } catch {
            // ignore
        }
    }

    get inventoryFields() {
        const fields = [
            { key: "programName", label: "Program" },
            { key: "storeName", label: "Store" },
            { key: "reason", label: "Reason", visible: !!this.inventories.filter(i => i.reason).length },
            { key: "location.name", label: "Location", visible: this.hasInventoryLocations },
            { key: "quantity" },
            { key: "allocated" },
            { key: "net", label: "Net", formatter: this.netFormatter },
            { key: "actions", label: "", class: "actions" },
        ];

        return fields.filter(f => f.visible !== false);
    }

    get drug(): DrugProduct {
        return {
            drugId: Number(this.packageId),
            source: this.drugSource,
        } as DrugProduct;
    }

    netFormatter(value: any, key: number, item: Inventory) {
        return (item.quantity || 0) - (item.allocated || 0);
    }

    submitInformation(e: any) {
        e.preventDefault();

        this.inventory.drugSource = Number(this.drugSource);
        this.inventory.packageId = Number(this.packageId);
        this.inventory.billingUnit = this.billingUnit;
        this.inventory.storeId = Number(this.store.id);
        this.inventory.programId = Number(this.program.id);
        this.inventory.reason = this.reasonSelection;
        this.inventory.location = this.location;
        this.inventory.useForFilling = this.useForFilling;

        const updateInventory = this.inventories.find(item => (item.location?.id ?? 0) == (this.inventory.location?.id ?? 0) &&
            item.storeId == this.inventory.storeId &&
            (item.programId || 0) == (this.inventory.programId || 0));
        if (updateInventory) {
            this.inventory.id = updateInventory.id;
        }
        axios.post<Inventory>('/Inventory/', this.inventory)
            .then(response => {
                if (response.data?.id) {
                    this.$notification(NotificationOptions.successSaveNotificationPreset("Inventory"));
                    this.$emit("update");
                }
                this.clearForm();
                this.editModal.hide();
            })
            .catch(error => {
                this.$notification(NotificationOptions.errorSaveNotificationPreset("Inventory", error));
                console.error("Error while updating inventory.", { error, response: error?.response });
            });
    }

    viewHistory(item: any) {
        this.showHistory = true;
        this.store = new Store({ id: item.storeId, name: item.storeName } as Store);
        this.program = new Program({ id: item.programId, name: item.programName } as Program);
    }

    updateInventory(item: Inventory) {
        this.store.id = item.storeId;
        this.program.id = Number(item.programId || 0);
        this.inventory.id = item.id;
        this.inventory.quantity = item.quantity;
        this.location = new InventoryLocation();
        this.location.id = item.location?.id ?? 0;
        this.useForFilling = item.useForFilling;
        this.editModal.show();
    }

    deleteInventoryConfirmDialog(item: Inventory) {
        this.$bvModal.msgBoxConfirm(`This inventory record associated to this drug will get deleted. Are you sure?`, {
            title: 'Confirm',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'YES',
            cancelTitle: 'NO',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: true
        })
            .then(value => {
                if (!value) return;
                this.deleteInventory(item);
            })
            .catch(err => {
                console.error("Error caught on Clear button.", err);
            });
    }

    deleteInventory(item: Inventory) {
        if (!item?.id) return;

        this.$http.delete(`inventory/${item.id}`)
            .then(res => {
                if (res.data) {
                    this.$notification(NotificationOptions.notificationOptionsPreset("Inventory record successfully deleted.", NotificationOptions.NotificationTypes.success));
                    this.$emit('removed');
                }
            })
            .catch(err => {
                this.$notification(NotificationOptions.error(err));
                console.error("Not possible to delete inventory record.", err);
            });
    }

    openForm() {
        this.clearForm();
        this.editModal.show();
    }

    clearForm() {
        this.inventory = new Inventory();
        this.store = new Store();
        this.program = new Program();
    }

    protected hasInventoryLocations = false;

    async loadHasInventoryLocations() {
        try {
            this.hasInventoryLocations = (await axios.get<boolean>("/inventory/has-inventory-locations")).data;
        } catch {
            //ignore
        }
    }

}
</script>

<style scoped>
::v-deep(.actions) {
    width: 100px;
}
</style>